export const MIN_DURATION_SEC = 3;
/** Max total target length (5 minutes). Longer runs are split into scenes and stitched. */
export const MAX_DURATION_SEC = 300;
/** Max seconds per single Kling API request (longer targets are stitched). */
export const KLING_CLIP_MAX_SEC = 15;

/** Duration choices: 3s, 5s, then 5s steps from 10–300. */
export function buildDurationOptions() {
  const values = new Set([MIN_DURATION_SEC, 5]);
  for (let d = 10; d <= MAX_DURATION_SEC; d += 5) values.add(d);
  return [...values].sort((a, b) => a - b);
}

export const DURATION_OPTIONS = buildDurationOptions();

/** Target duration shown in the UI (3–300s for all models). */
export function maxDurationForModel() {
  return MAX_DURATION_SEC;
}

export function minDurationForModel() {
  return MIN_DURATION_SEC;
}

/** Clamp slider / form values to allowed total duration (3–300s). */
export function clampDuration(value) {
  const n = Math.round(Number(value) || MIN_DURATION_SEC);
  return Math.min(MAX_DURATION_SEC, Math.max(MIN_DURATION_SEC, n));
}

/** Human-friendly duration label, e.g. `45s`, `1m 20s`, `5m`. */
export function formatDurationLabel(sec) {
  const n = Math.max(0, Math.round(Number(sec) || 0));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

/**
 * Per-model single-clip API limits. Targets above maxSec use auto-stitch.
 * @param {string} modelId
 * @param {{ hailuoResolution?: string; resolution?: string }} [opts]
 * @returns {{
 *   maxSec: number | null;
 *   minSec: number;
 *   allowed: number[] | null;
 *   stitchable: boolean;
 * }}
 */
export function getClipLimitForModel(modelId, opts = {}) {
  const id = String(modelId || "").toLowerCase();

  if (id.includes("motion-control")) {
    return { maxSec: null, minSec: MIN_DURATION_SEC, allowed: null, stitchable: false };
  }
  // Avatar length is driven by audio, not the duration slider.
  if (id.includes("kling-avatar") || id.includes("avatar")) {
    return { maxSec: null, minSec: MIN_DURATION_SEC, allowed: null, stitchable: false };
  }
  if (id.includes("veo")) {
    return { maxSec: 8, minSec: 8, allowed: [8], stitchable: true };
  }
  if (id.includes("omni-flash") || id.includes("omni")) {
    return { maxSec: 10, minSec: 4, allowed: [4, 6, 8, 10], stitchable: true };
  }
  if (id.includes("seedance")) {
    return { maxSec: 15, minSec: 4, allowed: null, stitchable: true };
  }
  if (id.includes("grok-imagine-video") || id.includes("grok-1.5")) {
    // Continuous 1–15s per clip on PoYo.
    return { maxSec: 15, minSec: 1, allowed: null, stitchable: true };
  }
  if (id.includes("grok")) {
    return { maxSec: 10, minSec: 6, allowed: [6, 10], stitchable: true };
  }
  if (id.includes("hailuo")) {
    const res = opts.hailuoResolution || opts.resolution || "768p";
    if (res === "1080p") {
      return { maxSec: 6, minSec: 6, allowed: [6], stitchable: true };
    }
    return { maxSec: 10, minSec: 6, allowed: [6, 10], stitchable: true };
  }
  if (id.includes("sora")) {
    if (id.includes("pro")) {
      return { maxSec: 25, minSec: 15, allowed: [15, 25], stitchable: true };
    }
    return { maxSec: 15, minSec: 10, allowed: [10, 15], stitchable: true };
  }
  if (id.includes("runway")) {
    return { maxSec: 10, minSec: 5, allowed: [5, 10], stitchable: true };
  }
  if (id.includes("wan")) {
    return { maxSec: 10, minSec: 2, allowed: null, stitchable: true };
  }
  if (id.includes("kling")) {
    return { maxSec: KLING_CLIP_MAX_SEC, minSec: MIN_DURATION_SEC, allowed: null, stitchable: true };
  }
  return { maxSec: KLING_CLIP_MAX_SEC, minSec: MIN_DURATION_SEC, allowed: null, stitchable: true };
}

/** Snap a duration to the nearest allowed value (or clamp to min/max). */
export function snapClipDuration(sec, limit) {
  const n = Math.round(Number(sec) || limit.minSec);
  if (limit.allowed?.length) {
    let best = limit.allowed[0];
    let bestDist = Math.abs(n - best);
    for (const a of limit.allowed) {
      const dist = Math.abs(n - a);
      if (dist < bestDist || (dist === bestDist && a > best)) {
        best = a;
        bestDist = dist;
      }
    }
    return best;
  }
  const max = limit.maxSec ?? MAX_DURATION_SEC;
  return Math.min(max, Math.max(limit.minSec, n));
}
